/* Eden: keep the Build Your Bundle discount code in step with the cart.
 * Tiers and codes come from theme settings (Bundle builder > Tiers JSON), rendered by
 * snippets/bundle-codes.liquid. Bundle lines carry the property _bundle = builder.
 *  - Bundle qty >= first tier min: apply that tier's code (subscription code if the bundle is on a
 *    selling plan). Other, non-bundle codes in the cart are kept (e.g. free shipping).
 *  - Otherwise: remove any bundle code, keep the rest.
 * Runs on the cart page load and after any cart change made via fetch/XHR; when the code changes
 * it reloads the cart page or re-renders the cart drawer. Public API:
 *   window.EdenBundleCodes.sync() -> Promise<boolean changed>
 *   window.EdenBundleCodes.refreshDrawer(open, variantId) -> Promise<boolean drawer found>
 */
(() => {
  if (window.EdenBundleCodes) return;

  const cfgEl = document.getElementById('eden-bundle-codes-config');
  if (!cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  let tiers;
  try { tiers = JSON.parse(cfg.tiersJson || '[]'); } catch (e) { tiers = []; }
  tiers = (Array.isArray(tiers) ? tiers : [])
    .map(t => ({ ...t, min: Number(t.min), max: t.max == null || Number(t.max) <= 0 ? null : Number(t.max) }))
    .filter(t => t.min > 0)
    .sort((a, b) => a.min - b.min);
  const allCodes = new Set(tiers.flatMap(t => [t.code, t.subscriptionCode]).filter(Boolean).map(c => String(c).toUpperCase()));
  const SYNC_HEADER = 'X-Eden-Bundle-Sync';

  const tierFor = qty => tiers.find(t => qty >= t.min && (t.max == null || qty <= t.max)) || null;
  const codeFor = (qty, subscribing) => {
    const t = tierFor(qty);
    if (!t) return null;
    return (subscribing ? t.subscriptionCode : t.code) || null;
  };

  let running = null;
  async function sync() {
    if (!cfg.enabled || !allCodes.size) return false;
    if (running) return running;
    running = (async () => {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json', [SYNC_HEADER]: '1' } }).then(r => r.json());
      const bundleLines = (cart.items || []).filter(i => i.properties && i.properties._bundle === 'builder');
      const qty = bundleLines.reduce((n, i) => n + i.quantity, 0);
      const subscribing = bundleLines.some(i => i.selling_plan_allocation);
      const desired = qty ? codeFor(qty, subscribing) : null;
      const current = (cart.discount_codes || []).map(d => String(d.code).toUpperCase());
      const others = current.filter(c => !allCodes.has(c));
      const next = desired ? [desired.toUpperCase(), ...others] : others;
      const same = next.length === current.length && next.every(c => current.includes(c));
      if (same) return false;
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [SYNC_HEADER]: '1' },
        body: JSON.stringify({ discount: next.join(',') }),
      });
      if (!res.ok) return false;
      document.dispatchEvent(new CustomEvent('eden:bundle-code-synced', { detail: { code: desired, codes: next } }));
      return true;
    })().finally(() => { running = null; });
    return running;
  }

  // Re-render the theme's cart drawer (Shrine cart-drawer) from the Section Rendering API so its
  // totals reflect the current code. open = false re-renders without opening it.
  // Returns false when the theme has no drawer (cart type set to page or notification).
  async function refreshDrawer(open = false, id = null) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function' || typeof drawer.getSectionsToRender !== 'function') return false;
    const ids = drawer.getSectionsToRender().map(s => s.id);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const sections = await origFetch(`${root}?sections=${ids.join(',')}`).then(r => r.json());
    if (open) drawer.classList.remove('is-empty');
    drawer.renderContents({ id, sections }, !open);
    return true;
  }

  const onCartPage = () => /^\/cart\/?$/.test(location.pathname);
  let timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      try {
        const changed = await sync();
        if (!changed) return;
        // The cart page renders totals server-side; reload once so the discount shows.
        if (onCartPage()) location.reload();
        else await refreshDrawer(false);
      } catch (e) { /* never block the cart */ }
    }, 350);
  }

  const isCartMutation = url => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  const hasSyncHeader = init => {
    const h = init && init.headers;
    if (!h) return false;
    if (typeof h.get === 'function') return !!h.get(SYNC_HEADER);
    return !!h[SYNC_HEADER];
  };

  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const p = origFetch.apply(this, arguments);
    if (isCartMutation(url) && !hasSyncHeader(init)) p.then(() => schedule(), () => {});
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__edenCartMutation = isCartMutation(url);
    return origOpen.apply(this, arguments);
  };
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__edenCartMutation) this.addEventListener('loadend', () => schedule());
    return origSend.apply(this, arguments);
  };

  window.EdenBundleCodes = { tiers, tierFor, codeFor, sync, refreshDrawer };
  if (onCartPage()) schedule();
})();
